import { CallingId } from './callings.js';

/**
 * Everything a Calling passive, skill node or mod can change about a player, resolved into one
 * flat struct. Additive fields are summed; multiplier fields are summed as fractions
 * (+0.1 and +0.05 -> 1.15x). The sim, combat and inventory code read this struct only.
 */
export interface PlayerModifiers {
  maxHp: number;
  maxStamina: number;
  speedMult: number;
  sprintDrainMult: number;
  recoilMult: number;
  spreadMult: number;
  damageMult: number;
  headshotBonus: number;
  reloadMult: number;
  magBonus: number;
  backpackSlots: number;
  quickSlots: number;
  healMult: number;
  reviveSpeed: number;
  noiseMult: number;
  staggerResist: number;
  modEffectMult: number;
  /** Ability cooldown reduction as a fraction (0.1 = 10% shorter). */
  cooldownMult: number;
  /** Per-ability modifier flags, e.g. 'bulwark.reflect'. */
  flags: string[];
}

export const BASE_MODIFIERS: PlayerModifiers = {
  maxHp: 0,
  maxStamina: 0,
  speedMult: 0,
  sprintDrainMult: 0,
  recoilMult: 0,
  spreadMult: 0,
  damageMult: 0,
  headshotBonus: 0,
  reloadMult: 0,
  magBonus: 0,
  backpackSlots: 0,
  quickSlots: 0,
  healMult: 0,
  reviveSpeed: 0,
  noiseMult: 0,
  staggerResist: 0,
  modEffectMult: 0,
  cooldownMult: 0,
  flags: [],
};

export type SkillNodeKind = 'stat' | 'ability' | 'keystone';

export interface SkillNode {
  id: string;
  calling: number;
  branch: 0 | 1 | 2;
  /** Position along the branch, 0..9; node n requires node n-1 of the same branch. */
  tier: number;
  kind: SkillNodeKind;
  name: string;
  desc: string;
  mods: Partial<PlayerModifiers>;
}

export interface SkillTree {
  calling: number;
  branches: [string, string, string];
  nodes: SkillNode[];
}

const BRANCHES: Record<number, [string, string, string]> = {
  [CallingId.Vanguard]: ['Wall', 'Charge', 'Endure'],
  [CallingId.Warden]: ['Mend', 'Rally', 'Ward'],
  [CallingId.Shade]: ['Hush', 'Sight', 'Knife'],
  [CallingId.Tinker]: ['Forge', 'Wire', 'Salvage'],
};

/** Stat nodes are shared templates; ability/keystone nodes are per branch. */
const STAT_TEMPLATES: { name: string; desc: string; mods: Partial<PlayerModifiers> }[] = [
  { name: 'Long Stride', desc: '+3% movement speed.', mods: { speedMult: 0.03 } },
  { name: 'Second Wind', desc: '+10 max stamina.', mods: { maxStamina: 10 } },
  { name: 'Steady Hands', desc: '-5% recoil.', mods: { recoilMult: -0.05 } },
  { name: 'Thick Hide', desc: '+8 max HP.', mods: { maxHp: 8 } },
  { name: 'Deep Pockets', desc: '+1 backpack slot.', mods: { backpackSlots: 1 } },
  { name: 'Quick Seat', desc: '-6% reload time.', mods: { reloadMult: -0.06 } },
  { name: 'Tight Cone', desc: '-4% spread.', mods: { spreadMult: -0.04 } },
  { name: 'Cool Head', desc: '-5% ability cooldowns.', mods: { cooldownMult: 0.05 } },
];

const ABILITY_NODES: Record<
  number,
  Record<
    number,
    {
      t4: SkillNode['mods'] & { name: string; desc: string };
      t7: { name: string; desc: string; mods: Partial<PlayerModifiers> };
      key: { name: string; desc: string; mods: Partial<PlayerModifiers> };
    }
  >
> = {
  [CallingId.Vanguard]: {
    0: {
      t4: { name: 'Riveted', desc: 'Bulwark has +150 HP.', flags: ['bulwark.hp150'] },
      t7: {
        name: 'Mirror Plate',
        desc: 'Bulwark reflects 20% of damage it absorbs.',
        mods: { flags: ['bulwark.reflect'] },
      },
      key: {
        name: 'The Wall Outlives You',
        desc: 'Bulwark persists 20 s after your death.',
        mods: { flags: ['bulwark.persist'] },
      },
    },
    1: {
      t4: { name: 'Longer Run', desc: 'Breach travels 12 m.', flags: ['breach.range12'] },
      t7: {
        name: 'Ram',
        desc: 'Breach deals 60 damage to Hymns it hits.',
        mods: { flags: ['breach.damage'] },
      },
      key: {
        name: 'Doorless',
        desc: 'Breach also opens Hymn-locked containers and staggers Sextons.',
        mods: { flags: ['breach.sexton'] },
      },
    },
    2: {
      t4: { name: 'Adrenaline', desc: 'Surge lasts 11 s.', flags: ['surge.long'] },
      t7: {
        name: 'Battle Trance',
        desc: 'Kills during Surge extend it 1 s.',
        mods: { flags: ['surge.extend'] },
      },
      key: {
        name: 'Unkillable Minute',
        desc: 'Surge grants 30 temporary HP.',
        mods: { flags: ['surge.shield'] },
      },
    },
  },
  [CallingId.Warden]: {
    0: {
      t4: { name: 'Fast Drone', desc: 'Mend Drone heals 8 HP/s.', flags: ['mend.fast'] },
      t7: { name: 'Wide Wings', desc: 'Mend Drone radius 9 m.', mods: { flags: ['mend.wide'] } },
      key: {
        name: 'Triage',
        desc: 'Mend Drone also revives one downed ally.',
        mods: { flags: ['mend.revive'] },
      },
    },
    1: {
      t4: { name: 'Quick Beacon', desc: 'Beacon revives after 3 s.', flags: ['beacon.fast'] },
      t7: { name: 'Bright Beacon', desc: 'Beacon lasts 40 s.', mods: { flags: ['beacon.long'] } },
      key: {
        name: 'Nobody Left',
        desc: 'Revived allies return with full stamina and 60 HP.',
        mods: { flags: ['beacon.full'] },
      },
    },
    2: {
      t4: { name: 'Quiet Dome', desc: 'Suppression Field lasts 8 s.', flags: ['suppress.long'] },
      t7: {
        name: 'Deaf Choir',
        desc: 'Hymns inside fire 75% slower.',
        mods: { flags: ['suppress.hard'] },
      },
      key: {
        name: 'Sanctuary',
        desc: 'Allies inside take 30% less damage.',
        mods: { flags: ['suppress.armor'] },
      },
    },
  },
  [CallingId.Shade]: {
    0: {
      t4: { name: 'Longer Veil', desc: 'Veil lasts 7 s.', flags: ['veil.long'] },
      t7: {
        name: 'Cold Blood',
        desc: 'Veil is not broken by firing a suppressed weapon.',
        mods: { flags: ['veil.fire'] },
      },
      key: {
        name: 'Nobody Home',
        desc: 'Veil also hides you from Pulse and Cantor charge lock.',
        mods: { flags: ['veil.total'] },
      },
    },
    1: {
      t4: { name: 'Far Pulse', desc: 'Pulse reaches 60 m.', flags: ['pulse.far'] },
      t7: {
        name: 'Lingering Pulse',
        desc: 'Pulse marks last 8 s.',
        mods: { flags: ['pulse.long'] },
      },
      key: {
        name: 'Reading the Room',
        desc: 'Pulse reveals loot containers and their tier.',
        mods: { flags: ['pulse.loot'] },
      },
    },
    2: {
      t4: { name: 'Cleaner Kill', desc: '+10% damage to unaware Hymns.', flags: ['knife.unaware'] },
      t7: {
        name: 'Quiet Steps',
        desc: 'Footsteps are silent to Hymns.',
        mods: { noiseMult: -0.5 },
      },
      key: {
        name: 'Executioner',
        desc: 'Silence doubles headshot damage.',
        mods: { flags: ['silence.exec'] },
      },
    },
  },
  [CallingId.Tinker]: {
    0: {
      t4: { name: 'Hardened Sentry', desc: 'Sentry has 200 HP.', flags: ['sentry.hp'] },
      t7: {
        name: 'Twin Sentry',
        desc: 'Two sentries may stand at once.',
        mods: { flags: ['sentry.twin'] },
      },
      key: {
        name: 'Overclock',
        desc: 'Sentry fires 50% faster and lasts 30 s.',
        mods: { flags: ['sentry.overclock'] },
      },
    },
    1: {
      t4: { name: 'Long Leash', desc: 'Hijack reaches 18 m.', flags: ['hijack.far'] },
      t7: { name: 'Loyal', desc: 'Hijacked Hymns last 20 s.', mods: { flags: ['hijack.long'] } },
      key: { name: 'Choirmaster', desc: 'Hijack a Sexton.', mods: { flags: ['hijack.sexton'] } },
    },
    2: {
      t4: { name: 'Scrapper', desc: '+25% materials from Hymn drops.', flags: ['salvage.more'] },
      t7: {
        name: 'Field Repair',
        desc: 'Fabricator also repairs Bulwarks and Sentries.',
        mods: { flags: ['fab.repair'] },
      },
      key: {
        name: 'Quartermaster',
        desc: 'Fabricator gives the squad a full mag reserve and 2 stims.',
        mods: { flags: ['fab.full'] },
      },
    },
  },
};

function buildTree(calling: number): SkillTree {
  const nodes: SkillNode[] = [];
  const branches = BRANCHES[calling]!;
  for (let b = 0 as 0 | 1 | 2; b < 3; b = (b + 1) as 0 | 1 | 2) {
    const ab = ABILITY_NODES[calling]![b]!;
    for (let tier = 0; tier < 10; tier++) {
      const id = `${calling}-${b}-${tier}`;
      if (tier === 4) {
        const { name, desc, ...mods } = ab.t4;
        nodes.push({ id, calling, branch: b, tier, kind: 'ability', name, desc, mods });
      } else if (tier === 7) {
        nodes.push({
          id,
          calling,
          branch: b,
          tier,
          kind: 'ability',
          name: ab.t7.name,
          desc: ab.t7.desc,
          mods: ab.t7.mods,
        });
      } else if (tier === 9) {
        nodes.push({
          id,
          calling,
          branch: b,
          tier,
          kind: 'keystone',
          name: ab.key.name,
          desc: ab.key.desc,
          mods: ab.key.mods,
        });
      } else {
        const t = STAT_TEMPLATES[(tier + b * 3 + calling) % STAT_TEMPLATES.length]!;
        nodes.push({
          id,
          calling,
          branch: b,
          tier,
          kind: 'stat',
          name: t.name,
          desc: t.desc,
          mods: t.mods,
        });
      }
    }
  }
  return { calling, branches, nodes };
}

export const SKILL_TREES: Record<number, SkillTree> = Object.fromEntries(
  Object.values(CallingId).map((c) => [c, buildTree(c)]),
);

/** True if `nodeId` may be unlocked given the already unlocked set (branch order). */
export function canUnlock(tree: SkillTree, unlocked: ReadonlySet<string>, nodeId: string): boolean {
  const node = tree.nodes.find((n) => n.id === nodeId);
  if (!node || unlocked.has(nodeId)) return false;
  if (node.tier === 0) return true;
  return unlocked.has(`${node.calling}-${node.branch}-${node.tier - 1}`);
}

/** Sum passives + unlocked node mods into one struct. */
export function resolveModifiers(
  passive: Partial<PlayerModifiers> | undefined,
  tree: SkillTree | undefined,
  unlocked: Iterable<string>,
): PlayerModifiers {
  const out: PlayerModifiers = { ...BASE_MODIFIERS, flags: [] };
  const add = (m: Partial<PlayerModifiers> | undefined) => {
    if (!m) return;
    for (const [k, v] of Object.entries(m)) {
      if (k === 'flags') out.flags.push(...(v as string[]));
      else {
        const rec = out as unknown as Record<string, number>;
        rec[k] = (rec[k] ?? 0) + (v as number);
      }
    }
  };
  add(passive);
  if (tree) {
    const set = new Set(unlocked);
    for (const n of tree.nodes) if (set.has(n.id)) add(n.mods);
  }
  return out;
}
